/**
 * CallLeadPopup — modal overlay shown while a direct-call (popup) lifecycle
 * is active. Rendered by CallLeadPopupProvider whenever `currentLead !== null`.
 *
 * Auto-dials on mount once the Twilio Device is `ready` (which is usually
 * already the case because TwilioProvider hoisted to app root pre-warms the
 * Device when the agent logs in). The popup stays open through the entire
 * call lifecycle and renders different bottom controls per state:
 *
 *   connecting / ringing       → Mute + End Call
 *   in-call                    → Mute + End Call
 *   ended / error              → Close (no Mute, no End Call)
 *
 * Closing the popup is the user's explicit signal to release the call slot
 * — the next CallLeadButton click anywhere in the app then works again. The
 * twilio context's resetCallState() flips the state back to "ready" so the
 * Device is immediately available for a new call.
 */
import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, PhoneOff, WifiOff, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useTwilioContext, CALL_END_REASON_LABELS } from "@/context/twilio.context";
import type { CallableLead } from "@/context/call.lead.popup.context";
import { getInitials } from "@/utils/strings";
import { cn } from "@/lib/utils";

type Props = {
  lead: CallableLead;
  onClose: () => void;
};

// State → pill label/color. `connecting` and `ringing` share the "CALLING"
// blue state — from the agent's perspective they're both "waiting for the
// other side to pick up". Pickup flips to "ACTIVE CALL" green; terminal
// states use neutral / red labels.
const STATE_PILLS: Record<
  string,
  { label: string; classes: string; dotClasses: string; pulse: boolean }
> = {
  initializing: {
    label: "PREPARING",
    classes: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    dotClasses: "bg-blue-400",
    pulse: false,
  },
  ready: {
    label: "READY",
    classes: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    dotClasses: "bg-blue-400",
    pulse: false,
  },
  connecting: {
    label: "CALLING",
    classes: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    dotClasses: "bg-blue-400",
    pulse: true,
  },
  ringing: {
    label: "CALLING",
    classes: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    dotClasses: "bg-blue-400",
    pulse: true,
  },
  "in-call": {
    label: "ACTIVE CALL",
    classes: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    dotClasses: "bg-emerald-400",
    pulse: true,
  },
  ended: {
    label: "CALL ENDED",
    classes: "bg-gray-500/15 text-gray-300 border-gray-500/30",
    dotClasses: "bg-gray-400",
    pulse: false,
  },
  error: {
    label: "CALL FAILED",
    classes: "bg-red-500/15 text-red-300 border-red-500/30",
    dotClasses: "bg-red-400",
    pulse: false,
  },
  "no-internet": {
    label: "CONNECTION LOST",
    classes: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    dotClasses: "bg-orange-400",
    pulse: false,
  },
  idle: {
    label: "IDLE",
    classes: "bg-gray-500/15 text-gray-300 border-gray-500/30",
    dotClasses: "bg-gray-400",
    pulse: false,
  },
};

const formatDuration = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const CallLeadPopup = ({ lead, onClose }: Props) => {
  const {
    state,
    isMuted,
    callStartedAt,
    errorMessage,
    isNetworkError,
    callEndReason,
    connect,
    mute,
    unmute,
    hangup,
    resetCallState,
  } = useTwilioContext();

  // Tick once a second while in-call so the displayed duration updates.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== "in-call") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  // Auto-dial once per popup mount. connect() runs the full lazy sequence
  // (mint lead-scoped token → build + register Device → dial), so there's no
  // pre-warmed "ready" state to wait for. `leadId` mints the token. This is a
  // WS-silent surface: NO `agent:engaging` / `agent:disengaged` is emitted for
  // popup calls (those only flow from the widget click + dedicated screen). No
  // `originPath` either — the popup is its own persistent indicator (modal,
  // follows the agent across navigation), so the floating ActiveCallIndicator
  // stays hidden while the popup owns the call.
  const autoDialedRef = useRef(false);
  useEffect(() => {
    if (autoDialedRef.current) return;
    if (!lead.phoneNumber) return;
    autoDialedRef.current = true;
    connect({
      phoneNumber: lead.phoneNumber,
      leadId: lead.id,
      leadName: lead.name,
    });
  }, [connect, lead.phoneNumber, lead.id, lead.name]);

  const isCallOver = state === "ended" || state === "error";
  const isCallActive =
    state === "connecting" || state === "ringing" || state === "in-call";

  const handleClose = () => {
    // Belt-and-suspenders: if the agent closes while still connected (e.g.
    // they hit the X before End Call), hang up first so the call doesn't
    // leak.
    if (isCallActive) {
      try { hangup(); } catch { /* noop */ }
    }
    resetCallState();
    onClose();
  };

  const handleEndCall = () => {
    hangup();
    // Don't close immediately — let the Twilio events transition state to
    // "ended" so the user sees the "CALL ENDED" pill briefly. The Close
    // button (or the X) is then the explicit dismiss.
  };

  const pill =
    state === "error" && isNetworkError
      ? STATE_PILLS["no-internet"]
      : (STATE_PILLS[state] ?? STATE_PILLS.idle);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Active call"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[#4A515A] bg-gradient-to-b from-[#1B1F28] to-[#0D1018] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* Soft inner glow at the top so the modal feels lifted off the dim
            backdrop without using an aggressive border. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-blue-500/5 to-transparent" />

        {/* ── Header: state pill + close ─────────────────────────────── */}
        <div className="relative flex items-center justify-between px-5 pt-5 pb-3">
          <span
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wider",
              pill.classes,
            )}
          >
            {pill.pulse ? (
              <span className="relative flex w-1.5 h-1.5">
                <span
                  className={cn(
                    "absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping",
                    pill.dotClasses,
                  )}
                />
                <span
                  className={cn("relative inline-flex w-1.5 h-1.5 rounded-full", pill.dotClasses)}
                />
              </span>
            ) : (
              <span className={cn("w-1.5 h-1.5 rounded-full", pill.dotClasses)} />
            )}
            {pill.label}
          </span>

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close call"
            className="flex items-center justify-center w-8 h-8 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body: avatar + identity + status ───────────────────────── */}
        <div className="relative px-5 pt-2 pb-6 flex flex-col items-center text-center">
          {/* Avatar with pulse ring while ringing */}
          <div className="relative mb-4">
            {(state === "connecting" || state === "ringing") && (
              <span className="absolute inset-0 rounded-2xl bg-emerald-500/30 animate-ping" />
            )}
            <Avatar className="relative h-20 w-20 rounded-2xl shadow-lg">
              <AvatarFallback className="rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-2xl font-semibold">
                {getInitials(lead.name)}
              </AvatarFallback>
            </Avatar>
          </div>

          <h2 className="text-xl font-semibold text-white">{lead.name}</h2>

          {(lead.role || lead.company) && (
            <p className="text-sm text-white/60 mt-1">
              {[lead.role, lead.company].filter(Boolean).join(" · ")}
            </p>
          )}

          <p className="text-sm text-white/40 mt-1 font-mono tracking-wide">
            {lead.phoneNumber}
          </p>

          {/* Status line — duration when connected, "Calling…" while ringing,
              ended/error copy in their respective states. */}
          <div className="mt-5 min-h-[2.5rem] flex flex-col items-center justify-center">
            {state === "in-call" && callStartedAt ? (
              <span className="text-white font-mono text-3xl tabular-nums font-light">
                {formatDuration(now - callStartedAt.getTime())}
              </span>
            ) : state === "connecting" || state === "ringing" ? (
              <span className="text-white/60 text-sm">Calling…</span>
            ) : state === "ended" ? (
              <span
                className={cn(
                  "text-sm",
                  callEndReason === "rejected" || callEndReason === "no-answer"
                    ? "text-amber-300"
                    : "text-white/60",
                )}
              >
                {callEndReason ? CALL_END_REASON_LABELS[callEndReason] : "Call ended"}
              </span>
            ) : state === "error" ? (
              <span className={cn(
                "flex flex-col items-center gap-1.5 text-sm max-w-xs",
                isNetworkError ? "text-orange-300" : "text-red-300",
              )}>
                {isNetworkError && <WifiOff className="w-5 h-5" />}
                {errorMessage ?? "Call failed"}
              </span>
            ) : (
              <span className="text-white/40 text-sm">Connecting your call…</span>
            )}
          </div>
        </div>

        {/* ── Controls: Mute + End Call (or Close if call is over) ────── */}
        <div className="relative border-t border-white/5 p-4 bg-black/20">
          {isCallOver ? (
            <Button
              type="button"
              onClick={handleClose}
              className="w-full bg-white/10 hover:bg-white/15 text-white font-medium"
            >
              Close
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={isMuted ? unmute : mute}
                  disabled={state !== "in-call"}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 transition-colors",
                    isMuted
                      ? "bg-white/15 border-white/20 text-white"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                    state !== "in-call" && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  <span className="text-xs font-medium">{isMuted ? "Unmute" : "Mute"}</span>
                </button>
              </div>

              <button
                type="button"
                onClick={handleEndCall}
                className="flex items-center justify-center gap-2 w-full rounded-xl bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-semibold py-3 shadow-lg shadow-red-500/20 transition-colors"
              >
                <PhoneOff className="w-5 h-5" />
                End call
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallLeadPopup;
